'use client';

import React, { useEffect, useMemo, useState } from 'react';
import useCloseFlyouts from '@/hooks/useCloseFlyouts';
import { Quote } from '@/types/entity/quote';
import { DashboardLinks } from '@/components/pages/dashboard/constants';
import Typography from '@/components/atoms/typography';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import Button from '@/components/atoms/button';
import { LineItem } from '@shared/types/cart';
import OrderSummary from '../order-summary';
import CartContent from './components/cart-content';
import { CartProps } from './types';
import CheckoutCTA from '../order-summary/components/checkout-cta';
import { CheckoutCTAProps } from '../order-summary/types';
import CartHeader from './components/cart-header';
import CartBody from './components/cart-body';
import InsufficientStockOverlay from './components/insufficient-overlay-alert';
import { useParams, useRouter } from 'next/navigation';

const newCartUI = true || localStorage.getItem('newcart') === 'true';

const Cart = ({
  paymentMethods,
  transaction,
  onRequestQuote,
  onAddCoupon,
  onRemoveCoupon,
  account,
  sum,
  vouchers,
  lineItems: lineItemsProp,
  ...props
}: CartProps) => {
  console.log("Line items props>>>>>>>>>>>>", lineItemsProp)
  const { onUpdateQuantity, onRemove, clearCart } = props;
  const [submittedQuote, setSubmittedQuote] = useState<Partial<Quote>>();
  const [showModal, setShowModal] = useState(false);
  const router = useRouter()
  const { locale } = useParams();
  const [lineItems, setLineItems] = useState<Array<LineItem & { deleted?: boolean }> | undefined>(lineItemsProp);

  useEffect(() => {
    if (!lineItemsProp) return;

    //Maintain the deleted items
    setLineItems((lineItems) => {
      if (newCartUI) {
        return lineItemsProp;
      }
      if (!lineItems) return lineItemsProp;

      const newItems = lineItemsProp.filter(
        (lineItem) => !lineItems.find((item) => lineItem.variant?.sku === item.variant?.sku),
      );

      return [
        ...lineItems.map((lineItem) => {
          const item = lineItemsProp.find((item) => lineItem.variant?.sku === item.variant?.sku);

          return { ...(item ?? lineItem), deleted: !item };
        }),
        ...newItems,
      ];
    });
  }, [lineItemsProp]);

  const [isOutOfStock, setIsOutOfStock] = useState(true);
  const isOutOfStockItemsPresent = !!lineItems?.find((lineItem: LineItem) => {
    const { variant, count = 0 } = lineItem;
    const availableQuantity = variant?.availableQuantity || 0;
    return count > availableQuantity;
  })

  useEffect(() => {
    setIsOutOfStock(isOutOfStockItemsPresent);
  }, [isOutOfStockItemsPresent]);


  const checkoutClickHandler = () => {
    if (isOutOfStock) {
      setShowModal(true);
      return;
    }
    router.push(`/${locale}/checkout`)
  };

  const closeFlyouts = useCloseFlyouts();

  const defaultCheckoutCTAProps: CheckoutCTAProps = useMemo(() => {
    return {
      text: "Go to checkout",
      link: `/${locale}/checkout`,
      onCheckout: closeFlyouts,
      onRequestQuote: async ({ buyerComment }) => {
        closeFlyouts();
        const quote = await onRequestQuote({ buyerComment });
        setSubmittedQuote(quote);
      },
      disabled: (lineItems ?? []).filter((item) => !item.deleted).some((item) => !item.variant?.isOnStock),
    };
  }, [closeFlyouts, onRequestQuote, lineItems]);
  console.log("Line Items here>>>>>>>>>>>>>>>>>>", lineItems)

  if (submittedQuote && submittedQuote.id) {
    return (
      <div className="relative min-h-[50vh] bg-[#F3EDE9] p-4 md:px-5 md:py-6 lg:p-12">
        <div className="grid w-full place-items-center gap-12 rounded-lg bg-white py-9">
          <div className="grid place-items-center gap-6">
            <Typography fontSize={20} className="text-gray-700">
              Your Quote has been submitted!
            </Typography>

            <Image src="/images/quote-submitted.jpeg" width={144} height={144} alt="Quote submitted" />
          </div>

          <p className="max-w-[720px] leading-loose text-[16px] text-center text-gray-600">
            A Sales Representative will provide you with a quote as soon as possible. You will be notified of quote updates on {account.email}
          </p>

          <Link link={DashboardLinks.quoteRequestDetail(submittedQuote.id)} underlineOnHover={false}>
            <Button size="l">View quote</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative min-h-[50vh] bg-[#F3EDE9]">  
        {newCartUI && (<>
          <div className="flex flex-col bg-white py-5 lg:items-start lg:bg-transparent lg:px-10">
            {(lineItems ?? []).length > 0 && (<CartHeader lineItems={lineItems} sum={sum} onCheckout={checkoutClickHandler} />)}
            <CartBody clearCart={clearCart} sum={sum} vouchers={vouchers} lineItems={lineItems} onUpdateQuantity={onUpdateQuantity} onRemove={onRemove} onCheckout={checkoutClickHandler} onAddCoupon={onAddCoupon} onRemoveCoupon={onRemoveCoupon} />
            <InsufficientStockOverlay
              show={showModal}
              toggleOverlay={(flag: boolean) => {
                setShowModal(flag);
              }}
            />
          </div>
        </>)}
        {!newCartUI && (<div className="flex flex-col bg-white py-4 md:py-6 lg:flex-row lg:items-start lg:gap-6 lg:bg-transparent lg:p-12">
          <CartContent
            lineItems={lineItems}
            className="grow bg-white px-4 py-3 md:px-6 lg:rounded-lg lg:p-9"
            {...props}
          />

          {(lineItems ?? []).length > 0 ? (
            <>
              <OrderSummary
                className="bg-white px-4 pb-3 md:px-6 md:pt-3 lg:mt-0 lg:rounded-lg lg:p-9 lg:px-12 lg:pb-11 xl:w-[432px] xl:shrink-0"
                title="Order Summary"
                paymentMethods={paymentMethods}
                button={<CheckoutCTA className="hidden w-full md:grid" {...defaultCheckoutCTAProps} />}
                transaction={transaction}
              />
              <CheckoutCTA
                onCheckout={checkoutClickHandler}
                className="sticky bottom-0 grid w-full border-t border-neutral-400 bg-white p-4 md:hidden"
                {...defaultCheckoutCTAProps}
              />
            </>
          ) : (
            <></>
          )}
        </div>)}
      </div>
    </>
  );
};

export default Cart;
